import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import {
  listAdminReports,
  upsertReport,
  deleteReport,
  type ReportRow,
} from "@/lib/reports.functions";

export const Route = createFileRoute("/admin/reports")({
  component: AdminReportsPage,
});

const EMPTY = {
  id: "",
  title: "",
  organization: "",
  year: new Date().getFullYear(),
  url: "",
  summary_tr: "",
  summary_en: "",
  category: "climate",
  topics_raw: "",
  region: "global",
  type: "report",
  language: "en",
  published: true,
  sort_order: 0,
};

const CATS = ["ai", "climate", "energy", "sustainability", "biodiversity", "economy", "society", "turkiye-politika", "other"];
const TYPES = ["report", "index", "dataset", "framework"];

function AdminReportsPage() {
  const list = useServerFn(listAdminReports);
  const upsert = useServerFn(upsertReport);
  const del = useServerFn(deleteReport);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-reports"],
    queryFn: () => list(),
  });

  const [form, setForm] = useState({ ...EMPTY });
  const [msg, setMsg] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const topics = form.topics_raw
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      const payload = {
        id: form.id || undefined,
        title: form.title,
        organization: form.organization,
        year: form.year ? Number(form.year) : null,
        url: form.url,
        summary_tr: form.summary_tr,
        summary_en: form.summary_en,
        category: form.category,
        topics,
        region: form.region || "global",
        type: form.type,
        language: form.language || "en",
        published: form.published,
        sort_order: Number(form.sort_order) || 0,
      };
      return upsert({ data: payload });
    },
    onSuccess: () => {
      setMsg("Kaydedildi ✓");
      setForm({ ...EMPTY });
      refetch();
      setTimeout(() => setMsg(null), 2000);
    },
    onError: (e: Error) => setMsg("Hata: " + e.message),
  });

  const onEdit = (r: ReportRow) => {
    setForm({
      id: r.id,
      title: r.title,
      organization: r.organization,
      year: r.year ?? new Date().getFullYear(),
      url: r.url,
      summary_tr: r.summary_tr,
      summary_en: r.summary_en,
      category: r.category,
      topics_raw: (r.topics ?? []).join(", "),
      region: r.region,
      type: r.type,
      language: r.language,
      published: r.published,
      sort_order: r.sort_order,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const onDelete = async (r: ReportRow) => {
    if (!confirm(`"${r.title}" silinsin mi?`)) return;
    await del({ data: { id: r.id } });
    refetch();
  };

  const rows = data ?? [];
  const isEditing = !!form.id;

  return (
    <div className="space-y-10">
      <section>
        <h1 className="font-serif text-3xl mb-2">Rapor Kütüphanesi</h1>
        <p className="text-sm text-ink/85 mb-6">
          Saygın küresel rapor, endeks ve araştırmalar. Yayımlananlar <code>/raporlar</code> ve{" "}
          <code>/reports</code> sayfalarında listelenir.
        </p>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="space-y-4 border border-border rounded-md p-5 bg-paper"
        >
          <div className="flex items-baseline justify-between">
            <h2 className="font-serif text-xl">
              {isEditing ? "Raporu düzenle" : "Yeni rapor"}
            </h2>
            {isEditing && (
              <button
                type="button"
                onClick={() => setForm({ ...EMPTY })}
                className="text-xs underline text-ink/60"
              >
                Yeniye geç
              </button>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="text-sm md:col-span-2">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">Başlık</span>
              <input
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full border border-border rounded px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">Kurum</span>
              <input
                value={form.organization}
                onChange={(e) => setForm({ ...form, organization: e.target.value })}
                placeholder="IPCC, WEF, IEA…"
                className="w-full border border-border rounded px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">Yıl</span>
              <input
                type="number"
                value={form.year}
                onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
                className="w-full border border-border rounded px-3 py-2"
              />
            </label>
            <label className="text-sm md:col-span-2">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">URL</span>
              <input
                required
                type="url"
                value={form.url}
                onChange={(e) => setForm({ ...form, url: e.target.value })}
                className="w-full border border-border rounded px-3 py-2 font-mono text-xs"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">Kategori</span>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-border rounded px-3 py-2 bg-paper"
              >
                {CATS.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">Tür</span>
              <select
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.target.value })}
                className="w-full border border-border rounded px-3 py-2 bg-paper"
              >
                {TYPES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>
            <label className="text-sm md:col-span-2">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">
                Konular (virgülle ayrılmış)
              </span>
              <input
                value={form.topics_raw}
                onChange={(e) => setForm({ ...form, topics_raw: e.target.value })}
                placeholder="climate, policy, AI"
                className="w-full border border-border rounded px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">Bölge</span>
              <input
                value={form.region}
                onChange={(e) => setForm({ ...form, region: e.target.value })}
                className="w-full border border-border rounded px-3 py-2"
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">Sıralama (büyük = üst)</span>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
                className="w-full border border-border rounded px-3 py-2"
              />
            </label>
          </div>

          <label className="block text-sm">
            <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">Özet (TR)</span>
            <textarea
              value={form.summary_tr}
              onChange={(e) => setForm({ ...form, summary_tr: e.target.value })}
              rows={3}
              className="w-full border border-border rounded px-3 py-2"
            />
          </label>
          <label className="block text-sm">
            <span className="block mb-1 text-xs uppercase tracking-[0.15em] text-ink/60">Summary (EN)</span>
            <textarea
              value={form.summary_en}
              onChange={(e) => setForm({ ...form, summary_en: e.target.value })}
              rows={3}
              className="w-full border border-border rounded px-3 py-2"
            />
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => setForm({ ...form, published: e.target.checked })}
            />
            Yayında
          </label>

          <div className="flex items-center gap-4">
            <button
              type="submit"
              disabled={save.isPending}
              className="px-5 py-2.5 bg-ink text-paper rounded-full text-xs uppercase tracking-[0.2em] disabled:opacity-50"
            >
              {save.isPending ? "Kaydediliyor..." : isEditing ? "Güncelle" : "Ekle"}
            </button>
            {msg && <span className="text-sm text-ink/70">{msg}</span>}
          </div>
        </form>
      </section>

      <section>
        <h2 className="font-serif text-2xl mb-4">Mevcut raporlar ({rows.length})</h2>
        {isLoading ? (
          <p className="text-sm text-ink/60">Yükleniyor...</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-ink/60">Henüz rapor eklenmedi.</p>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((r) => (
              <li key={r.id} className="py-4 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 rounded ${
                        r.published ? "bg-moss/10 text-moss" : "bg-ink/10 text-ink/60"
                      }`}
                    >
                      {r.published ? "Yayında" : "Taslak"}
                    </span>
                    <span className="text-xs text-ink/85">{r.category}</span>
                    <span className="text-xs text-ink/85">· {r.organization}</span>
                    {r.year && <span className="text-xs text-ink/85">· {r.year}</span>}
                  </div>
                  <div className="font-serif text-lg mt-1 truncate">{r.title}</div>
                </div>
                <button
                  onClick={() => onEdit(r)}
                  className="text-xs underline text-ink/70 hover:text-ink"
                >
                  Düzenle
                </button>
                <button
                  onClick={() => onDelete(r)}
                  className="text-xs text-ink/85 hover:text-clay"
                >
                  Sil
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
